import {
  ApiClient,
  ApiPaginationParams,
  ApiPaginationResult,
} from '~/apiclient/apiclient'
import { SpeakerApi } from '~/apiclient/apispeakers'
import { AudioCodecType, VideoCodecType } from '~/models/media'
import { Users } from '~/models/users/users'
import { clone, PromiseAll } from '~/assets/ts/utils/misc'
import { Webcast } from '~/models/webcast'
import { Broadcaster } from '~/models/broadcaster'
import {
  SermonEncodingOptions,
  UploadMediaType,
} from '~/apiclient/apisermoneditor'
import { jsDateToApiDateStr } from '~/assets/ts/utils/date'
import {
  CanUseFacebookIntegration,
  CanUseGoogleIntegration,
} from '~/assets/ts/utils/social'
import { SocialAccountsApi } from '~/apiclient/apibroadcasters'

export const WebcastReplayWeeks = 2
export function GetWebcastReplayDuration(vue: Vue) {
  const n = WebcastReplayWeeks
  return vue.$tc('{n} week | {n} weeks', n, { n })
}

export interface WebcastApiOriginalStreamInfo {
  width: number
  height: number
  audioCodec: AudioCodecType
  videoCodec: VideoCodecType
  bitrate: number
}

export interface WebcastApi {
  archivedSermons?: string[]
  archivedSermonID?: string
  audioStreamURL: string
  boostedWebcast?: boolean
  broadcasterID: string
  broadcasterImageURL?: string
  broadcasterImageURLResizable?: string
  broadcasterAlbumArtURL?: string
  broadcasterLat: number
  broadcasterLocation: string
  broadcasterLong: number
  broadcasterMinister: string
  completeStreamJSON?: string
  displayName: string
  endTime?: number
  eventStreamURL: string
  videoStreamPreviewURL?: string
  audioEventStreamURL: string
  followedBroadcaster: boolean
  group?: string
  isLive?: boolean
  isPublic?: boolean
  isPending?: boolean
  miles?: number
  originalStreamInfo?: WebcastApiOriginalStreamInfo
  peakListenerCount: number
  previewImageURL: string
  priorityBoostedWebcast?: boolean
  reconnectCount?: number
  resizablePreviewImageURL?: string
  startTime: number
  totalListenLineCount?: number
  totalTuneInCount: number
  videoStreamURL: string
  webcastID: number
  webcastTitle?: string
  webcastDescription?: string
  webcastSpeakerName?: string
  webcastSpeaker?: SpeakerApi
  password?: string | null
  passwordProtected?: boolean
  webcastReplay?: boolean
}

export interface WebcastsInProgressOptions extends ApiPaginationParams {
  broadcasterID?: string
  group?: string
  state?: string
  country?: string
  vacantPulpit?: boolean
  showHidden?: boolean
  showPending?: boolean
  boostedOnly?: boolean
  ignoreFollowed?: boolean
  password?: string
}

export interface WebcastOptions {
  broadcasterID: string
  webcastID: number
  password?: string
}

export const WebcastDashboardOptions = {
  showHidden: true,
  bypassPassword: true,
  isDashboard: true,
  showPending: true,
  cacheTTL: 0,
  key: 'broadcaster',
} as WebcastsInProgressOptions

const placeholderPw = '`'

export async function getWebcastsInProgress(
  this: ApiClient,
  params = {} as WebcastsInProgressOptions
): Promise<WebcastApi[]> {
  if (params.showHidden === undefined && !params.broadcasterID) {
    const users = (this.$context as any).$users as Users
    if (users.user?.admin) {
      params.showHidden = true
    }
  }
  // password protected webcasts are hidden without passing _something_
  // in for the password parameter
  if (params.broadcasterID) {
    params.password = params.password || placeholderPw
  }
  // https://git.sermonaudio.com/project-management/main/-/wikis/Discover-Tab-APIs#get-webcasts-in-progress
  // https://git.sermonaudio.com/project-management/main/-/wikis/APIs-for-SermonAudio-2.0#lite-model-objects
  const { data } = await this.$axios.get('node/webcasts/lite', {
    params,
  })
  return data.results
}

export async function getWebcast(
  this: ApiClient,
  params: WebcastOptions
): Promise<WebcastApi> {
  const { data } = await this.$axios.get(
    `node/broadcasters/${params.broadcasterID}/webcasts/${params.webcastID}`,
    {
      params: {
        lite: true,
        password: params.password,
      },
    }
  )
  return data
}

export interface WebcastHistoryOptions {
  broadcasterID?: string
  page?: number
  pageSize?: number
  excludeWebcastsInProgress?: boolean
  minimumDurationSeconds?: number
  showHidden?: boolean
  isDashboard?: boolean
  searchKeyword?: string
}

export async function getWebcastHistory(
  this: ApiClient,
  params: WebcastHistoryOptions = {}
): Promise<ApiPaginationResult<WebcastApi>> {
  if (!params.searchKeyword) {
    params.searchKeyword = undefined
  }
  if (params.excludeWebcastsInProgress === undefined) {
    params.excludeWebcastsInProgress = true
  }
  const { data } = await this.$axios.get('node/webcasts/history', {
    params,
  })
  return data
}

export interface WebcastCountOptions {
  broadcasterID?: string
  showHidden?: boolean
  password?: string
}

export async function getWebcastCount(
  this: ApiClient,
  params: WebcastCountOptions = {}
): Promise<number> {
  if (params.showHidden === undefined) {
    if (params.broadcasterID) {
      params.showHidden = true
    } else {
      const users = (this.$context as any).$users as Users
      if (users.user?.admin) {
        params.showHidden = true
      }
    }
  }
  const { data } = await this.$axios.get('node/webcasts/count', {
    params,
  })
  return data
}

interface BroadcasterWebcastMetadataApi {
  defaultWebcastTitle: string
  defaultWebcastDescription: string
  defaultWebcastSpeakerName: string
  defaultWebcastPassword: string
  defaultWebcastReplay: boolean
}

export async function getWebcastMetadata(
  this: ApiClient,
  broadcasterID: string
): Promise<BroadcasterWebcastMetadataApi> {
  const { data } = await this.$axios.get(
    `node/broadcasters/${broadcasterID}/webcast_metadata`,
    {
      params: {
        key: 'broadcaster',
      },
    }
  )
  return data
}

interface WebcastMetadataApi {
  webcastTitle: string
  webcastDescription: string
  webcastSpeakerName: string
  webcastPassword: string
  webcastReplay: boolean
}

export interface WebcastMetadataParams {
  broadcasterID: string
  webcastID?: number
  title: string
  description: string
  speakerName: string
  password: string
  replay: boolean
}

export async function updateWebcastMetadata(
  this: ApiClient,
  {
    broadcasterID,
    webcastID,
    title,
    description,
    speakerName,
    password,
    replay,
  }: WebcastMetadataParams
) {
  const defaults = this.$axios.put(
    `node/broadcasters/${broadcasterID}/webcast_metadata`,
    {
      defaultWebcastTitle: title || undefined,
      defaultWebcastDescription: description || undefined,
      defaultWebcastSpeakerName: speakerName || undefined,
      defaultWebcastPassword: password,
      defaultWebcastReplay: replay,
    } as BroadcasterWebcastMetadataApi
  )
  const apis = [defaults]

  if (webcastID) {
    const webcast = this.$axios.put(`webcasts/${webcastID}/webcast_metadata`, {
      webcastTitle: title || undefined,
      webcastDescription: description || undefined,
      webcastSpeakerName: speakerName || undefined,
      webcastPassword: password,
      webcastReplay: replay,
    } as WebcastMetadataApi)
    apis.push(webcast)
  }

  return await PromiseAll(apis)
}

export async function toggleWebcastReplayable(
  this: ApiClient,
  webcast: Webcast
) {
  await this.$axios.put(`webcasts/${webcast.id}/webcast_metadata`, {
    webcastTitle: webcast.title,
    webcastSpeakerName: webcast.speakerName,
    webcastDescription: webcast.description,
    webcastPassword: webcast.password,
    // unlisted is the flipped version of replay, so we can use it directly to toggle
    webcastReplay: webcast.unlisted,
  } as WebcastMetadataApi)
}

export async function resetStreamTokenId(
  this: ApiClient,
  broadcasterID: string
): Promise<void> {
  return await this.patchBroadcaster(broadcasterID, {
    resetStreamToken: true,
  })
}

export enum MulticastStatus {
  Stopped = 'stopped',
  Starting = 'starting',
  Running = 'running',
  Stopping = 'stopping',
}

export enum MulticastPlatform {
  SermonAudio = 'sa',
  Facebook = 'facebook',
  Twitter = 'twitter',
  Youtube = 'youtube',
  Twitch = 'twitch',
  Other = 'other',
  /** Tight integration platforms */
  YoutubeApi = 'youtube-api',
  /** Tight integration platforms */
  FacebookApi = 'facebook-api',
  /** Tight integration platforms */
  TwitterApi = 'twitter-api',
}

export interface MulticastEndpointApi {
  multicast_id: number
  stream_platform: MulticastPlatform
  stream_label: string
  stream_url: string
  stream_key: string
  status: MulticastStatus
  broadcaster_id: string
  disabled: boolean
  start_time?: number | null
  end_time?: number | null
  last_alive_time?: number | null
  auto_start: boolean
  // rtmp_url: string
  // job_name: string
  // pod_name: string
  // origin_url: string | null
}

export interface FakeMulticastEndpointParams {
  broadcasterID: string
  platform: MulticastPlatform
  autoStart?: boolean
  disabled?: boolean
}

export function CreateFakeMulticastEndpoint(
  params: FakeMulticastEndpointParams
): MulticastEndpointApi {
  const { platform } = params
  return {
    multicast_id: -platform.length,
    broadcaster_id: params.broadcasterID,
    disabled: !!params.disabled,
    stream_platform: platform,
    status: MulticastStatus.Stopped,
    stream_label: '',
    stream_url: '',
    stream_key: '',
    auto_start: !!params.autoStart,
  }
}

export function CustomMulticastEndpoints(endpoints: MulticastEndpointApi[]) {
  return endpoints.filter(
    (c) =>
      ![MulticastPlatform.FacebookApi, MulticastPlatform.YoutubeApi].includes(
        c.stream_platform
      )
  )
}

function integratedEndpoint(
  platform: MulticastPlatform,
  broadcasterID: string,
  endpoints: MulticastEndpointApi[],
  disabled: boolean
) {
  const existing = endpoints.find((c) => c.stream_platform === platform)
  if (existing) return existing
  return CreateFakeMulticastEndpoint({
    broadcasterID,
    platform,
    disabled,
  })
}

export function FacebookIntegratedEndpoint(
  broadcasterID: string,
  endpoints: MulticastEndpointApi[],
  accounts?: SocialAccountsApi
) {
  return integratedEndpoint(
    MulticastPlatform.FacebookApi,
    broadcasterID,
    endpoints,
    !CanUseFacebookIntegration(accounts?.facebook)
  )
}

export function GoogleIntegratedEndpoint(
  broadcasterID: string,
  endpoints: MulticastEndpointApi[],
  accounts?: SocialAccountsApi
) {
  return integratedEndpoint(
    MulticastPlatform.YoutubeApi,
    broadcasterID,
    endpoints,
    !CanUseGoogleIntegration(accounts?.google)
  )
}

export async function getMulticastEndpoints(
  this: ApiClient,
  broadcasterID: string,
  includeSystem = false
): Promise<MulticastEndpointApi[]> {
  const { data } = await this.$axios.get(
    `node/broadcasters/${broadcasterID}/multicast_list`,
    {
      params: {
        include_system: includeSystem,
      },
    }
  )
  return data.results
}

function sanitizeMulticastInfo(channel: MulticastEndpointApi) {
  const copy = clone(channel) as Record<string, any>
  delete copy.multicast_id
  return copy
}
export async function createMulticastEndpoint(
  this: ApiClient,
  channel: MulticastEndpointApi
): Promise<MulticastEndpointApi> {
  const { data } = await this.$axios.post(
    `node/broadcasters/${channel.broadcaster_id}/multicast`,
    sanitizeMulticastInfo(channel)
  )
  return data
}

function multicastUrl(channel: MulticastEndpointApi, url = '') {
  return `node/broadcasters/${channel.broadcaster_id}/multicast/${channel.multicast_id}${url}`
}

export async function updateMulticastEndpoint(
  this: ApiClient,
  channel: MulticastEndpointApi
): Promise<void> {
  return await this.$axios.patch(
    multicastUrl(channel),
    sanitizeMulticastInfo(channel)
  )
}

export async function deleteMulticastEndpoint(
  this: ApiClient,
  channel: MulticastEndpointApi
): Promise<void> {
  return await this.$axios.delete(multicastUrl(channel))
}

export async function startMulticastEndpoint(
  this: ApiClient,
  channel: MulticastEndpointApi
): Promise<void> {
  return await this.$axios.post(multicastUrl(channel, '/start'))
}

export async function stopMulticastEndpoint(
  this: ApiClient,
  channel: MulticastEndpointApi
): Promise<void> {
  return await this.$axios.post(multicastUrl(channel, '/stop'))
}

export async function restartMulticastEndpoint(
  this: ApiClient,
  channel: MulticastEndpointApi
): Promise<void> {
  return await this.$axios.post(multicastUrl(channel, '/restart'))
}

export enum WebcastHealthStatus {
  Healthy = 'healthy',
  /** Disconnects over threshold (2 currently), but metrics are within range
   * OR Failure to connect to multicast stream (and never successfully reconnected) */
  Warning = 'warning',
  /** Metrics are outside normal range */
  Unhealthy = 'unhealthy',
  /** Metrics are outside normal range & disconnects over threshold (2 currently) */
  Critical = 'critical',
  /** Used client side to display a historical health status */
  Offline = 'offline',
}

export enum WebcastHealthEventType {
  Connected = 'connected',
  Disconnected = 'disconnected',
  FramerateLow = 'framerate-low',
  FramerateNormal = 'framerate-normal',
  FramerateHigh = 'framerate-high',
  BitrateLow = 'bitrate-low',
  BitrateNormal = 'bitrate-normal',
  BitrateHigh = 'bitrate-high',
  AudioCodecChanged = 'audio-codec-changed',
  VideoCodecChanged = 'video-codec-changed',
  SpeedLow = 'speed-low',
  SpeedNormal = 'speed-normal',

  /** transcoder has successfully started to ingest stream (i.e. ffmpeg can 'hook-up' to the stream) */
  StreamConnected = 'stream-connected',
  /** transcoder has failed to ingest stream (likely cause keyframe rate is variable or too high) (i.e. ffmpeg cannot hook-up to the stream) */
  StreamConnectFail = 'stream-connect-fail',

  RestreamConnected = 'restream-connected',
  RestreamDisconnected = 'restream-disconnected',
  RestreamConnectFail = 'restream-connect-fail',

  ProfileMismatch = 'profile-mismatch',

  /** Added client side for distinguishing the first connect from a reconnect */
  Reconnected = 'reconnected',
  /** Added client side for distinguishing the final disconnect from a temporary disconnect */
  Interrupted = 'interrupted',

  /** Added client side for distinguishing the first connect from a reconnect */
  RestreamReconnected = 'restream-reconnected',
  /** Added client side for distinguishing the final disconnect from a temporary disconnect */
  RestreamInterrupted = 'restream-interrupted',
}

export interface WebcastHealthEvent {
  eventTimestamp: number
  webcastEventType: WebcastHealthEventType
  threshold: string | null
  currentValue: number | string | null
  multicastID: number | null
  streamLabel: string | null
  streamPlatform: MulticastPlatform | null
  /** Currently unused */
  info: string | null
}

export interface WebcastHealthApi {
  webcastID: number
  bitRate: number
  width: number
  height: number
  frameRate: number
  speed: number
  videoCodec: string
  audioCodec: string
  startTimestamp: number
  reconnectCount: number
  health: WebcastHealthStatus
  events: WebcastHealthEvent[]
}

export async function webcastHealth(
  this: ApiClient,
  webcastID: number
): Promise<WebcastHealthApi> {
  const { data } = await this.$axios.get(`/webcasts/${webcastID}/health`)
  return data
}

export interface StatsRegion {
  regions: Record<string, number>
  total: number
}

export type StatsLocations = Record<string, StatsRegion>

export interface LiveWebcastStats {
  locations: StatsLocations
  totalCountries: number
  webcastID: number
}

export async function liveWebcastStats(
  this: ApiClient,
  webcastID: number
): Promise<LiveWebcastStats> {
  const { data } = await this.$axios.get(`/webcasts/${webcastID}/stats`, {
    params: {
      key: 'broadcaster',
    },
  })
  return data
}

export interface ArchiveWebcastParams {
  webcast: Webcast
  broadcaster: Broadcaster
  languageCode: string
  encodingOptions: SermonEncodingOptions
  sermonId?: string
  sermonTitle?: string
}

export async function archiveWebcast(
  this: ApiClient,
  params: ArchiveWebcastParams
): Promise<string> {
  const webcast = params.webcast
  let sermonID = params.sermonId
  if (!sermonID) {
    const sermon = await this.createSermon(
      params.broadcaster,
      params.languageCode,
      {
        fullTitle: params.sermonTitle,
        preachDate: jsDateToApiDateStr(webcast.StartDate),
        speakerID: webcast.speaker?.id,
        archivedWebcastID: webcast.id,
        moreInfoText: webcast.description,
        languageCode: params.languageCode,
      }
    )
    sermonID = sermon.sermonID
  } else {
    await this.editSermon(sermonID, {
      archivedWebcastID: webcast.id,
    })
  }
  await this.updateEncodingOptions(sermonID, params.encodingOptions, false)
  await this.triggerEncodingJob({
    sermonID,
    webcastID: webcast.id,
    job: webcast.AudioOnly
      ? UploadMediaType.AutoArchiveAudio
      : UploadMediaType.AutoArchiveVideo,
  })
  return sermonID
}
